import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  PencilSimple, Trash, Plus, X, Check, 
  ListNumbers, ArrowUp, ArrowDown, Question
} from "@phosphor-icons/react";
import { Button } from "../shared/Button";
import { Input, Select, Textarea } from "../shared/Input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPOS_TRABAJO = [
  { id: "puertas", nombre: "🚪 Puertas" },
  { id: "suelos", nombre: "🏠 Suelos/Parquet" },
  { id: "pintura_paramentos", nombre: "🎨 Pintura Paredes" },
  { id: "pintura_carpinteria", nombre: "🚪 Pintura Puertas/Ventanas" },
  { id: "ceramica", nombre: "🔲 Cerámica" },
  { id: "cocina", nombre: "🍳 Cocina" },
  { id: "bano", nombre: "🚿 Baño" },
  { id: "mantenimiento", nombre: "🔧 Mantenimiento" },
  { id: "mamparas", nombre: "🚿 Mamparas" },
  { id: "electricidad", nombre: "⚡ Electricidad" },
  { id: "fontaneria", nombre: "💧 Fontanería" }
];

export const EditorPreguntas = ({ adminToken }) => {
  const [tipoSeleccionado, setTipoSeleccionado] = useState("puertas");
  const [preguntas, setPreguntas] = useState([]);
  const [modalEdicion, setModalEdicion] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tipoSeleccionado) {
      cargarPreguntas();
    }
  }, [tipoSeleccionado]);

  const cargarPreguntas = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/preguntas-diagnostico?tipo_trabajo=${tipoSeleccionado}`);
      const data = await res.json();
      setPreguntas(data);
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const guardarPregunta = async () => {
    try {
      const resp = modalEdicion.id
        ? await fetch(`${API}/admin/preguntas-diagnostico/${modalEdicion.id}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${adminToken}`
            },
            body: JSON.stringify(modalEdicion)
          })
        : await fetch(`${API}/admin/preguntas-diagnostico`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${adminToken}`
            },
            body: JSON.stringify({
              ...modalEdicion,
              tipo_trabajo: tipoSeleccionado
            })
          });
      if (!resp.ok) throw new Error(await resp.text());
      cargarPreguntas();
      setModalEdicion(null);
    } catch (err) {
      console.error("Error guardando:", err);
      alert("❌ No se pudo guardar la pregunta. Probá de nuevo — tus cambios NO se guardaron.");
    }
  };

  const eliminarPregunta = async (id) => {
    if (!window.confirm("¿Eliminar esta pregunta?")) return;
    try {
      const resp = await fetch(`${API}/admin/preguntas-diagnostico/${id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${adminToken}` }
      });
      if (!resp.ok) throw new Error(await resp.text());
      cargarPreguntas();
    } catch (err) {
      console.error("Error:", err);
      alert("❌ No se pudo eliminar la pregunta. Probá de nuevo.");
    }
  };

  // Intercambia el "orden" de esta pregunta con el de su vecina (arriba o
  // abajo) — antes solo se cambiaba el número de la pregunta tocada, sin
  // tocar la vecina, así que dos preguntas terminaban con el mismo orden.
  const cambiarOrden = async (idx, direccion) => {
    const otroIdx = direccion === "up" ? idx - 1 : idx + 1;
    if (otroIdx < 0 || otroIdx >= preguntas.length) return;
    const actual = preguntas[idx];
    const otra = preguntas[otroIdx];
    try {
      const [resp1, resp2] = await Promise.all([
        fetch(`${API}/admin/preguntas-diagnostico/${actual.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${adminToken}` },
          body: JSON.stringify({ ...actual, orden: otra.orden })
        }),
        fetch(`${API}/admin/preguntas-diagnostico/${otra.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${adminToken}` },
          body: JSON.stringify({ ...otra, orden: actual.orden })
        }),
      ]);
      if (!resp1.ok || !resp2.ok) throw new Error("Fallo al reordenar");
      cargarPreguntas();
    } catch (err) {
      console.error("Error:", err);
      alert("❌ No se pudo reordenar. Probá de nuevo.");
    }
  };

  return (
    <div className="container pt-4 pb-24">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-h2 font-display">
          <Question size={28} /> Editor de Preguntas Diagnóstico
        </h1>
        <Button variant="primary" onClick={() => setModalEdicion({
          pregunta: "",
          tipo_respuesta: "texto",
          opciones: [],
          unidad: "",
          obligatorio: true,
          orden: preguntas.length + 1,
          campo_clave: ""
        })}>
          <Plus size={20} /> Nueva Pregunta
        </Button>
      </div>

      {/* Aviso real: esta pantalla guarda en una colección propia
          (preguntas_diagnostico) que el Diagnóstico Inteligente del cliente
          NO lee — ese usa una lógica fija por módulo dentro del código
          (DiagnosticoInteligente.js). Lo que edites o crees acá no cambia
          nada de lo que ve el cliente todavía. */}
      <div className="card p-3 mb-4" style={{ background: "#FEF3C7", border: "1px solid #F59E0B" }}>
        <div className="text-small font-bold" style={{ color: "#92400E" }}>⚠️ Todavía no conectado</div>
        <div className="text-tiny" style={{ color: "#92400E" }}>
          Lo que edites acá no cambia las preguntas reales que ve el cliente en Diagnóstico — esa lógica hoy vive fija en el código, no en esta base de datos. Es una pantalla en desarrollo.
        </div>
      </div>

      {/* Selector de tipo */}
      <div className="card p-4 mb-4">
        <Select
          label="Tipo de Trabajo"
          value={tipoSeleccionado}
          onChange={(e) => setTipoSeleccionado(e.target.value)}
          options={TIPOS_TRABAJO.map(t => ({ value: t.id, label: t.nombre }))}
        />
      </div>

      {/* Lista preguntas */}
      {loading ? (
        <div className="text-center py-8">Cargando...</div>
      ) : preguntas.length === 0 ? (
        <div className="card p-8 text-center text-secondary">
          No hay preguntas para este tipo de trabajo
        </div>
      ) : (
        <div className="space-y-2">
          {preguntas.map((pregunta, idx) => (
            <motion.div
              key={pregunta.id}
              layout
              className="card p-4"
            >
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-1">
                  <button
                    className="icon-button"
                    onClick={() => cambiarOrden(idx, "up")}
                    disabled={idx === 0}
                  >
                    <ArrowUp size={16} />
                  </button>
                  <button
                    className="icon-button"
                    onClick={() => cambiarOrden(idx, "down")}
                    disabled={idx === preguntas.length - 1}
                  >
                    <ArrowDown size={16} />
                  </button>
                </div>
                
                <div className="text-h3 font-bold">{pregunta.orden}</div>
                
                <div className="flex-1">
                  <div className="font-bold">{pregunta.pregunta}</div>
                  <div className="text-small text-secondary">
                    Tipo: {pregunta.tipo_respuesta}
                    {pregunta.unidad && ` · Unidad: ${pregunta.unidad}`}
                    {pregunta.obligatorio && " · Obligatorio"}
                  </div>
                  {pregunta.opciones && pregunta.opciones.length > 0 && (
                    <div className="text-tiny text-secondary mt-1">
                      Opciones: {pregunta.opciones.join(", ")}
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    className="icon-button"
                    onClick={() => setModalEdicion(pregunta)}
                  >
                    <PencilSimple size={20} />
                  </button>
                  <button
                    className="icon-button"
                    onClick={() => eliminarPregunta(pregunta.id)}
                    style={{ color: "var(--error)" }}
                  >
                    <Trash size={20} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal Edición */}
      <AnimatePresence>
        {modalEdicion && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            onClick={() => setModalEdicion(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="modal-content"
              style={{ maxWidth: "600px", maxHeight: "90vh", overflow: "auto" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-h3 font-display">
                  {modalEdicion.id ? "Editar Pregunta" : "Nueva Pregunta"}
                </h2>
                <button className="icon-button" onClick={() => setModalEdicion(null)}>
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-3">
                <Textarea
                  label="Pregunta"
                  rows="2"
                  value={modalEdicion.pregunta}
                  onChange={(e) => setModalEdicion({ ...modalEdicion, pregunta: e.target.value })}
                  placeholder="¿Cuántos m² necesitas cubrir?"
                />

                <Select
                  label="Tipo de Respuesta"
                  value={modalEdicion.tipo_respuesta}
                  onChange={(e) => setModalEdicion({ ...modalEdicion, tipo_respuesta: e.target.value })}
                  options={[
                    { value: "numero", label: "Número" },
                    { value: "texto", label: "Texto libre" },
                    { value: "boolean", label: "Sí/No" },
                    { value: "multiple", label: "Selección múltiple" }
                  ]}
                />

                {modalEdicion.tipo_respuesta === "multiple" && (
                  <Textarea
                    label="Opciones (una por línea)"
                    rows="4"
                    value={modalEdicion.opciones?.join("\n") || ""}
                    onChange={(e) => setModalEdicion({ 
                      ...modalEdicion, 
                      opciones: e.target.value.split("\n").filter(o => o.trim()) 
                    })}
                    placeholder="Opción 1&#10;Opción 2&#10;Opción 3"
                  />
                )}

                <div className="grid grid-cols-2 gap-2">
                  <Input
                    label="Unidad (opcional)"
                    value={modalEdicion.unidad}
                    onChange={(e) => setModalEdicion({ ...modalEdicion, unidad: e.target.value })}
                    placeholder="m², ud, ml..."
                  />
                  <Input
                    label="Campo clave"
                    value={modalEdicion.campo_clave}
                    onChange={(e) => setModalEdicion({ ...modalEdicion, campo_clave: e.target.value })}
                    placeholder="cantidad, estado..."
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={modalEdicion.obligatorio}
                    onChange={(e) => setModalEdicion({ ...modalEdicion, obligatorio: e.target.checked })}
                  />
                  <label>Pregunta obligatoria</label>
                </div>

                <div className="flex gap-2 mt-4">
                  <Button variant="primary" className="flex-1" onClick={guardarPregunta}>
                    <Check size={20} /> Guardar
                  </Button>
                  <Button variant="outline" onClick={() => setModalEdicion(null)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
